import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Spinner, Badge, Modal, Form } from 'react-bootstrap';
import { FaCheckCircle, FaBolt, FaGift, FaStar, FaLock } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import AgencyNavbar from '../../Pages/Navbar/AgencyNavbar';
import BaseUrl from '../../ApiServices/BaseUrl';
import loadRazorpay from '../../ApiServices/loadRazorpay';
import './AgencyJobSubscriptionPlans.css';

const PLAN_ICONS = { free: <FaGift />, gold: <FaStar />, pay_per_click: <FaBolt />, silver: <FaStar /> };
const PLAN_GRADIENTS = {
  free: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
  gold: 'linear-gradient(135deg, #f4a903 0%, #d48f00 100%)',
  pay_per_click: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
  silver: 'linear-gradient(135deg, #8E9AAF 0%, #5C6B7A 100%)'
};
const PLAN_COLORS = { free: '#6c757d', gold: '#f4a903', pay_per_click: '#0d6efd', silver: '#8E9AAF' };
const PLAN_LABELS = { free: 'FREE', gold: 'GOLD', pay_per_click: 'PAY PER USE', silver: 'SILVER' };
const PLAN_RANK = { free: 0, pay_per_click: 1, silver: 2, gold: 3 };

const AgencyJobSubscriptionPlans = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user')) || {};
  const agencyId = user.id || '';
  const agencyCountry = user.country || 'AE';

  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const [couponCodes, setCouponCodes] = useState({});
  const [couponStatuses, setCouponStatuses] = useState({});
  const [applyingCoupons, setApplyingCoupons] = useState({});

  const [walletBalance, setWalletBalance] = useState(0);
  const [topUpModal, setTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [processingTopUp, setProcessingTopUp] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${BaseUrl}/api/agency/subscription-plans?country=${agencyCountry}&category=posting`);
      setPlans(res.data);
    } catch {
      toast.error('Failed to load subscription plans');
    }
  }, [agencyCountry]);

  const fetchCurrentSub = useCallback(async () => {
    if (!agencyId) return;
    try {
      const res = await axios.get(`${BaseUrl}/api/agency/subscription/${agencyId}?category=posting`);
      setCurrentSub(res.data.subscription);
      setUsage(res.data.usage || []);
    } catch { /* no sub yet */ }
  }, [agencyId]);

  const fetchWalletInfo = useCallback(async () => {
    if (!agencyId) return;
    try {
      const res = await axios.get(`${BaseUrl}/api/agency/wallet/${agencyId}?category=posting`);
      setWalletBalance(res.data.balance || 0);
    } catch (err) {
      console.error('Error fetching wallet:', err);
    }
  }, [agencyId]);

  useEffect(() => {
    Promise.all([fetchPlans(), fetchCurrentSub(), fetchWalletInfo()]).finally(() => setLoading(false));
  }, [fetchPlans, fetchCurrentSub, fetchWalletInfo]);

  const handleTopUp = async () => {
    if (!topUpAmount || isNaN(topUpAmount) || topUpAmount <= 0) {
      Swal.fire('Error', 'Please enter a valid amount', 'error');
      return;
    }
    setProcessingTopUp(true);
    try {
      const amountInPaise = Math.round(parseFloat(topUpAmount) * 100);
      const orderRes = await axios.post(`${BaseUrl}/api/orders`, { amount: amountInPaise, currency: 'INR' });
      const { order_id, currency, amount: orderAmount } = orderRes.data;
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: orderAmount, currency,
        name: 'Gulf Worker', description: 'Wallet Top-up', order_id,
        handler: async (response) => {
          const verifyRes = await axios.post(`${BaseUrl}/api/verify-payment`, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          });
          if (verifyRes.data.success) {
            await axios.post(`${BaseUrl}/api/agency/wallet/topup`, {
              agency_id: agencyId, amount: parseFloat(topUpAmount),
              category: 'posting',
              description: `Razorpay Top-up (${response.razorpay_payment_id})`
            });
            Swal.fire('Success', 'Wallet topped up successfully', 'success');
            setTopUpModal(false); setTopUpAmount(''); fetchWalletInfo();
          } else { Swal.fire('Error', 'Payment verification failed', 'error'); }
        },
        prefill: { name: user.agency_name || '', email: user.email || '', contact: user.official_phone || '' },
        theme: { color: '#f39c12' },
        modal: { ondismiss: () => { setProcessingTopUp(false); toast.warn('Payment cancelled'); } }
      };
      const isRazorpayReady = await loadRazorpay();
      if (!isRazorpayReady) {
        toast.error('Payment gateway failed to load. Please try again.');
        return;
      }
      new window.Razorpay(options).open();
    } catch (err) {
      toast.error(`Top-up failed: ${err.response?.data?.details || err.message}`);
    } finally { setProcessingTopUp(false); }
  };

  const isSubscriptionActive = () => currentSub?.status === 'active' && new Date(currentSub.expiry_date) >= new Date();
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const getUsage = (featureKey) => { const fm = usage.find(u => u.feature_key === featureKey); return fm ? fm.units_used : 0; };
  const isDowngrade = (plan) => {
    if (!isSubscriptionActive() || !currentSub) return false;
    return (PLAN_RANK[plan.type] ?? 0) < (PLAN_RANK[currentSub.plan_type] ?? 0);
  };

  const handleSubscribe = async (plan) => {
    if (isSubscriptionActive() && currentSub?.plan_id === plan.id) { toast.info('You already have this plan active.'); return; }
    if (isDowngrade(plan)) { toast.error('Downgrade not allowed. Please wait for your current plan to expire.'); return; }
    if (isSubscriptionActive()) {
      let switchMsg = `Your current plan "${currentSub.plan_name}" will be cancelled and replaced with "${plan.name}".`;
      if (plan.type === 'gold' && currentSub.plan_type === 'pay_per_click') switchMsg += '\n\n✅ After upgrading to Gold, your wallet balance will NOT be deducted for usage.';
      if (plan.type === 'silver' && currentSub.plan_type === 'pay_per_click') switchMsg += '\n\n✅ After upgrading to Silver, your wallet balance will NOT be deducted for usage.';
      const result = await Swal.fire({ title: 'Upgrade Plan?', text: switchMsg, icon: 'question', showCancelButton: true, confirmButtonText: 'Yes, upgrade' });
      if (!result.isConfirmed) return;
    }
    const pricing = plan.country_pricing;
    setSelectedPlanId(plan.id);
    const basePrice = pricing ? parseFloat(pricing.base_price) : 0;

    // Calculate final price with coupon if applied
    let finalAmountInPaise = Math.round(basePrice * 100);
    let paymentStatus = 'paid';

    if (couponStatuses[plan.id] && couponStatuses[plan.id].applied && (plan.type === 'gold' || plan.type === 'silver')) {
      const discountMultiplier = (100 - couponStatuses[plan.id].discount) / 100;
      finalAmountInPaise = Math.round((basePrice * discountMultiplier) * 100);
      if (finalAmountInPaise === 0) {
        paymentStatus = 'coupon_applied';
      }
    }

    if (plan.type === 'free' || !pricing || basePrice === 0 || finalAmountInPaise === 0) {
      await doSubscribe(plan, paymentStatus === 'coupon_applied' ? 'coupon_applied' : 'free', 0, null, null);
      return;
    }

    setSubscribing(true);
    try {
      const orderRes = await axios.post(`${BaseUrl}/api/orders`, { amount: finalAmountInPaise, currency: 'INR' });
      const { order_id, currency, amount: orderAmount } = orderRes.data;
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY, amount: orderAmount, currency,
        name: 'Gulf Worker', description: `${plan.name} Subscription`, order_id,
        handler: async (response) => {
          const verifyRes = await axios.post(`${BaseUrl}/api/verify-payment`, {
            razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          });
          if (verifyRes.data.success) await doSubscribe(plan, 'paid', orderAmount / 100, order_id, response.razorpay_payment_id);
          else toast.error('Payment verification failed');
        },
        prefill: { name: user.name || '', email: user.email || '', contact: user.mobile || '' },
        theme: { color: '#3399cc' },
        modal: { ondismiss: () => { setSubscribing(false); setSelectedPlanId(null); toast.warn('Payment cancelled'); } }
      };
      const isRazorpayReady = await loadRazorpay();
      if (!isRazorpayReady) {
        toast.error('Payment gateway failed to load. Please try again.');
        setSubscribing(false);
        setSelectedPlanId(null);
        return;
      }
      new window.Razorpay(options).open();
    } catch (e) {
      toast.error(`Payment failed: ${e.response?.data?.details || e.message}`);
      setSubscribing(false); setSelectedPlanId(null);
    }
  };

  const doSubscribe = async (plan, paymentStatus, amountPaid, orderId, paymentId) => {
    setSubscribing(true);
    try {
      const pricing = plan.country_pricing;
      await axios.post(`${BaseUrl}/api/agency/subscribe`, {
        agency_id: agencyId, plan_id: plan.id,
        country_code: pricing?.country_code || agencyCountry, currency: pricing?.currency || 'AED',
        base_price_paid: amountPaid, payment_status: paymentStatus,
        razorpay_order_id: orderId, razorpay_payment_id: paymentId
      });
      await Swal.fire({ title: 'Subscription Activated!', text: `You are now subscribed to "${plan.name}". Valid for ${plan.validity_days} days.`, icon: 'success' });
      await fetchCurrentSub(); await fetchPlans();
    } catch (e) { toast.error(e.response?.data?.error || 'Subscription failed'); }
    finally { setSubscribing(false); setSelectedPlanId(null); }
  };

  if (loading) return (<><AgencyNavbar /><Container style={{ marginTop: '90px' }}><div className="text-center py-5"><Spinner animation="border" /></div></Container></>);

  const handleApplyCoupon = async (plan) => {
    if (!agencyId) { Swal.fire('Login Required', 'Please login to subscribe.', 'info'); return; }
    const code = couponCodes[plan.id] || '';
    if (!code) {
      toast.error('Please enter a coupon code');
      return;
    }
    setApplyingCoupons(prev => ({ ...prev, [plan.id]: true }));
    setCouponStatuses(prev => ({ ...prev, [plan.id]: null }));
    try {
      const res = await axios.post(`${BaseUrl}/api/coupon/validate`, { code, plan_type: plan.type });
      if (res.data.success) {
        const discount = parseFloat(res.data.discount_percentage);
        setCouponStatuses(prev => ({ ...prev, [plan.id]: { applied: true, message: res.data.message, discount } }));
        setCouponCodes(prev => ({ ...prev, [plan.id]: '' }));

        if (discount === 100) {
          toast.success('Coupon Applied! Activating your free plan...');
          await doSubscribe(plan, 'coupon_applied', 0, null, null);
        } else {
          toast.success(`Coupon Applied: ${res.data.message}`);
        }
      } else {
        setCouponStatuses(prev => ({ ...prev, [plan.id]: { applied: false, message: res.data.message, discount: 0 } }));
        toast.error(res.data.message);
      }
    } catch (err) {
      setCouponStatuses(prev => ({ ...prev, [plan.id]: { applied: false, message: err.response?.data?.message || 'Invalid coupon', discount: 0 } }));
      toast.error(err.response?.data?.message || 'Invalid coupon');
    } finally {
      setApplyingCoupons(prev => ({ ...prev, [plan.id]: false }));
    }
  };

  return (
    <>
      <AgencyNavbar />
      <Container className="py-4 asp-compact-cards" style={{ marginTop: '70px', maxWidth: '1600px' }}>
        <div className="asp-page-header">
          <h2 className="asp-page-title">Job & Candidate Posting Subscription Plans</h2>
          <p className="asp-page-subtitle">Subscribe to post jobs and reach candidates</p>
        </div>

        {isSubscriptionActive() && (
          <div className="asp-active-banner">
            <FaCheckCircle className="asp-active-banner-icon" />
            <span className="asp-active-banner-text">
              <strong>Active Plan: {currentSub.plan_name}</strong> — Expires on <strong>{formatDate(currentSub.expiry_date)}</strong>
            </span>
          </div>
        )}

        <Row className="g-4 justify-content-center">
          {plans
            .sort((a, b) => (PLAN_RANK[a.type] ?? 99) - (PLAN_RANK[b.type] ?? 99))
            .map(plan => {
            const pricing = plan.country_pricing;
            const isCurrent = currentSub?.plan_id === plan.id && isSubscriptionActive();
            const color = PLAN_COLORS[plan.type] || '#333';
            const gradient = PLAN_GRADIENTS[plan.type] || PLAN_GRADIENTS.free;
            const basePrice = pricing ? parseFloat(pricing.base_price) : 0;

            return (
              <Col key={plan.id} xs={12} md={6} lg={3}>
                <div className={`asp-plan-card ${isCurrent ? 'asp-current' : ''}`}>
                  {/* Gradient Header */}
                  <div className="asp-card-header" style={{ background: gradient }}>
                    {isCurrent && <span className="asp-active-tag">✓ Active</span>}
                    <div className="asp-card-icon">{PLAN_ICONS[plan.type]}</div>
                    <h4 className="asp-card-name">{plan.name}</h4>
                    <Badge className="asp-type-badge">{PLAN_LABELS[plan.type]}</Badge>
                    <div className="asp-validity">{plan.validity_days} days validity</div>
                  </div>

                  <div className="asp-card-body">
                    {/* Pricing */}
                    <div className="asp-pricing-section">
                      {plan.type === 'free' ? (
                        <div className="asp-price-free">FREE</div>
                      ) : (plan.type === 'gold' || plan.type === 'silver') ? (
                        <>
                          <div><span className="asp-price-amount">{basePrice > 0 ? basePrice.toLocaleString() : '0'}</span>
                            <span className="asp-price-currency">{pricing?.currency || 'USD'}</span></div>
                          <div className="asp-price-period">per {plan.validity_days} days</div>
                        </>
                      ) : (
                        <div className="asp-price-per-use">Pay per action from wallet</div>
                      )}
                    </div>

                    {/* Features */}
                    <div className="asp-section-title">Features & Limits</div>
                    <div className="asp-features-list">
                      {/* Free Plan Static Features merged into the same list */}
                      {plan.type === 'free' && [
                        'Free Profile Creation',
                        'View Profiles',
                        'Send & Receive Messages (Free)',
                        'Search Matches',
                        'Edit Profile Anytime',
                        'Save / Shortlist Profiles',
                        'Match Alerts & Notifications',
                        'Upload Photos',
                      ].map((feat, idx) => (
                        <div key={`static-${idx}`} className="asp-feature-row">
                          <div className="asp-feature-left">
                            <FaCheckCircle className="asp-feature-icon" style={{ color: '#4caf50' }} />
                            <span className="asp-feature-label">{feat}</span>
                          </div>
                        </div>
                      ))}

                      {/* Gold Static Features */}
                      {plan.type === 'gold' && [
                        'See Who Viewed Your Profile',
                        'Boost Profile (Weekly Free)',
                        'Top Listing Priority',
                        'Premium Gold Badge Display',
                        'Priority in Match Suggestions'
                      ].map((feat, idx) => (
                        <div key={`gold-static-${idx}`} className="asp-feature-row">
                          <div className="asp-feature-left">
                            <FaCheckCircle className="asp-feature-icon" style={{ color: '#f4a903' }} />
                            <span className="asp-feature-label">{feat}</span>
                          </div>
                        </div>
                      ))}

                      {/* Silver Static Features */}
                      {plan.type === 'silver' && [
                        'See Who Viewed Your Profile',
                        'Boost Profile (Monthly Free)',
                        'Top Listing (Limited Days)',
                        'Verified Badge (Standard)',
                        'Priority Match Suggestions',
                      ].map((feat, idx) => (
                        <div key={`silver-static-${idx}`} className="asp-feature-row">
                          <div className="asp-feature-left">
                            <FaCheckCircle className="asp-feature-icon" style={{ color: '#8E9AAF' }} />
                            <span className="asp-feature-label">{feat}</span>
                          </div>
                        </div>
                      ))}

                      {/* Pay Per Click Static Features */}
                      {plan.type === 'pay_per_click' && [
                        { label: 'Boost Profile', price: '0' },
                        { label: 'Top Listing (Per Day)', price: '0' },
                        { label: 'Verified Badge (After KYC - Paid)', price: '0' }
                      ].map((feat, idx) => (
                        <div key={`ppc-static-${idx}`} className="asp-feature-row d-flex align-items-center justify-content-between">
                          <div className="asp-feature-left d-flex align-items-center gap-2">
                            <FaCheckCircle className="asp-feature-icon" style={{ color: '#0d6efd' }} />
                            <span className="asp-feature-label">{feat.label}</span>
                          </div>
                          <div className="asp-feature-right d-flex align-items-center gap-2">
                            <span className="asp-feature-limit-badge" style={{ background: '#f0f0f0', color: '#555', fontSize: '0.72rem', padding: '2px 8px', borderRadius: '6px', fontWeight: 600 }}>Unlimited</span>
                            <span style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600, whiteSpace: 'nowrap' }}>{pricing?.currency || 'AED'} {feat.price}/use</span>
                          </div>
                        </div>
                      ))}

                      {plan.features?.map(f => {
                        const used = getUsage(f.feature_key);
                        return (
                          <div key={f.id} className="asp-feature-row d-flex align-items-center justify-content-between">
                            <div className="asp-feature-left d-flex align-items-center gap-2">
                              <FaCheckCircle className="asp-feature-icon" style={{ color: plan.type === 'free' ? '#4caf50' : color }} />
                              <span className="asp-feature-label">{f.feature_label.replace(/\s*\((Employer|Candidate|Agency|Job\s*Seeker)\)/gi, '').replace('Job Poster Contact', 'Employer Contact')}</span>
                            </div>
                            <div className="asp-feature-right d-flex align-items-center gap-2">
                              {isCurrent && f.usage_limit > 0 ? (
                                <span className="asp-feature-usage" style={{ fontSize: '0.72rem', fontWeight: 600, color: '#2e7d32' }}>{used}/{f.usage_limit}</span>
                              ) : (
                                <span className="asp-feature-limit-badge" style={{ background: '#f0f0f0', color: '#555', fontSize: '0.72rem', padding: '2px 8px', borderRadius: '6px', fontWeight: 600 }}>
                                  {f.usage_limit === 0 ? 'Unlimited' : `${f.usage_limit} uses`}
                                </span>
                              )}
                              {plan.type === 'pay_per_click' && (
                                <span style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  {pricing?.currency || ''} {Number(f.price_per_unit)}/use
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {(!plan.features || plan.features.length === 0) &&
                        !['free', 'pay_per_click', 'silver', 'gold'].includes(plan.type) && (
                          <p style={{ color: '#999', textAlign: 'center', padding: '16px 0', fontSize: '0.85rem' }}>No features included</p>
                        )}
                    </div>

                    <div className="asp-card-footer">
                      {/* Gold Plan PREMIUM BENEFITS */}
                      {plan.type === 'gold' && (
                        <div className="asp-wallet-box" style={{
                          background: '#fff9e6', border: '1px solid #ffeeba', borderRadius: '8px',
                          padding: '14px 16px', textAlign: 'left'
                        }}>
                          <div style={{ fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: '#f4a903' }}>
                            <FaBolt style={{ fontSize: '0.85rem' }} /> <span>PREMIUM BENEFITS</span>
                          </div>
                          <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.6', color: '#856404', fontSize: '0.78rem' }}>
                            <li>Maximum visibility for all posts</li>
                            <li>Priority support & faster verification</li>
                            {/* <li>Full access to all premium tools</li> */}
                          </ul>
                        </div>
                      )}

                      {/* PPC Plan Benefits */}
                      {plan.type === 'pay_per_click' && (
                        <div className="asp-wallet-box" style={{
                          background: '#eef6ff', border: '1px solid #c2dbff', borderRadius: '8px',
                          padding: '14px 16px', textAlign: 'left'
                        }}>
                          <div style={{ fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: '#0d6efd' }}>
                            <FaBolt style={{ fontSize: '0.85rem' }} /> <span>BENEFITS</span>
                          </div>
                          <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.6', color: '#0a58ca', fontSize: '0.78rem' }}>
                            {/* <li>No monthly commitment</li> */}
                            <li>Pay only for what you use</li>
                            <li>Wallet never expires</li>
                          </ul>
                        </div>
                      )}

                      {/* Silver Plan Important Note */}
                      {plan.type === 'silver' && (
                        <div className="asp-wallet-box" style={{
                          background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px',
                          padding: '14px 16px', textAlign: 'left'
                        }}>
                          <div style={{ fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: '#495057' }}>
                            <FaLock style={{ fontSize: '0.85rem' }} /> <span>IMPORTANT NOTE</span>
                          </div>
                          <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.6', color: '#6c757d', fontSize: '0.78rem' }}>
                            <li>Contact unlock has monthly limits</li>
                            <li>Best for active & serious members</li>
                          </ul>
                        </div>
                      )}

                      {/* Free Plan Important Note - Using asp-wallet-box class for perfect alignment */}
                      {plan.type === 'free' && (
                        <div className="asp-wallet-box" style={{
                          background: '#fef9f0', border: '1px solid #f0dbb8', borderRadius: '8px',
                          padding: '14px 16px', textAlign: 'left'
                        }}>
                          <div style={{ fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: '#7a5c2e' }}>
                            <FaLock style={{ fontSize: '0.85rem' }} /> <span>IMPORTANT NOTE</span>
                          </div>
                          <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.6', color: '#7a5c2e', fontSize: '0.78rem' }}>
                            <li>Free users cannot see contact details</li>
                            <li>Only subscribed members can contact directly</li>
                          </ul>
                        </div>
                      )}

                      {/* Wallet (PPC only) */}
                      {plan.type === 'pay_per_click' && (
                        <div className="asp-wallet-box">
                          <div className="asp-wallet-label">Wallet Balance</div>
                          <div>
                            <span className="asp-wallet-amount">{Number(walletBalance)}</span>
                            <span className="asp-wallet-currency">{pricing?.currency || 'AED'}</span>
                          </div>
                          <Button variant="link" className="asp-wallet-topup-btn p-0 text-decoration-none"
                            onClick={(e) => { e.stopPropagation(); setTopUpModal(true); }}>
                            + Add Money To Wallet
                          </Button>
                        </div>
                      )}

                      {/* Coupon Box (Gold/Silver) */}
                      {(plan.type === 'gold' || plan.type === 'silver') && (
                        <div className="asp-wallet-box" style={{ background: '#fffcf5', border: '1px dashed #f4a903' }}>
                          <div className="asp-wallet-label mb-2" style={{ color: '#d48f00' }}>Have a Coupon?</div>
                          <div className="d-flex gx-2">
                            <Form.Control
                              size="sm"
                              type="text"
                              placeholder="Enter Code"
                              value={couponCodes[plan.id] || ''}
                              onChange={(e) => setCouponCodes(prev => ({ ...prev, [plan.id]: e.target.value }))}
                              style={{ borderRadius: '4px 0 0 4px', border: '1px solid #f4a903', boxShadow: 'none' }}
                              disabled={couponStatuses[plan.id]?.applied}
                            />
                            <Button
                              variant="warning"
                              size="sm"
                              style={{ borderRadius: '0 4px 4px 0', background: '#f4a903', color: '#fff', border: 'none' }}
                              onClick={() => handleApplyCoupon(plan)}
                              disabled={applyingCoupons[plan.id] || couponStatuses[plan.id]?.applied}
                            >
                              {applyingCoupons[plan.id] ? <Spinner size="sm" animation="border" /> : couponStatuses[plan.id]?.applied ? 'Applied' : 'Apply'}
                            </Button>
                          </div>
                          {couponStatuses[plan.id] && !couponStatuses[plan.id].applied && (
                            <div className="text-danger mt-1" style={{ fontSize: '0.75rem' }}>{couponStatuses[plan.id].message}</div>
                          )}
                          {couponStatuses[plan.id] && couponStatuses[plan.id].applied && (
                            <div className="text-success mt-1 fw-bold" style={{ fontSize: '0.75rem' }}>
                              {couponStatuses[plan.id].discount}% Discount Applied!
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action Button */}
                      <button className={`asp-activate-btn ${isCurrent ? 'asp-activated' : ''} ${isDowngrade(plan) ? 'asp-downgrade-disabled' : ''}`}
                        style={{ background: isCurrent ? undefined : isDowngrade(plan) ? '#ccc' : gradient, cursor: isDowngrade(plan) ? 'not-allowed' : undefined }}
                        onClick={() => handleSubscribe(plan)}
                        disabled={(subscribing && selectedPlanId === plan.id) || isDowngrade(plan)}>
                        {subscribing && selectedPlanId === plan.id
                          ? <><Spinner size="sm" animation="border" /> Processing...</>
                          : isCurrent ? <><FaCheckCircle /> Plan Activated</>
                            : isDowngrade(plan) ? 'Activate This Plan'
                              : 'Activate This Plan'}
                      </button>
                    </div>
                  </div>
                </div>
              </Col>
            );
          })}
          {plans.length === 0 && <Col><p className="text-center text-muted py-5">No plans available at this time.</p></Col>}
        </Row>

        {/* Top-up Modal */}
        <Modal show={topUpModal} onHide={() => setTopUpModal(false)} centered className="asp-topup-modal">
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">💰 Top-up Wallet</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-3">Add funds to your wallet for Pay Per Use features.</p>

            <div className="asp-amount-grid mb-4">
              {[500, 1000, 1500, 2000].map(amount => (
                <div
                  key={amount}
                  className={`asp-amount-box ${topUpAmount === amount.toString() ? 'active' : ''}`}
                  onClick={() => setTopUpAmount(amount.toString())}
                >
                  <span className="asp-amount-value">{amount}</span>
                  <span className="asp-amount-currency">AED</span>
                </div>
              ))}
            </div>

            <Form.Group className="mb-4">
              {/* <Form.Label className="fw-bold small text-muted">Amount</Form.Label> */}
              <Form.Control type="number" placeholder="Enter amount" className="asp-topup-input"
                value={topUpAmount} onChange={(e) => setTopUpAmount(e.target.value)} />
            </Form.Group>
            <button className="asp-topup-confirm-btn" onClick={handleTopUp} disabled={processingTopUp}>
              {processingTopUp ? <><Spinner size="sm" animation="border" className="me-2" /> Processing…</> : 'Confirm Top-up'}
            </button>
          </Modal.Body>
        </Modal>

        <div className="text-center mt-5 mb-4">
          <Button variant="outline-secondary" className="asp-back-btn shadow-sm" onClick={() => navigate(-1)}>← Go Back</Button>
        </div>
      </Container>
    </>
  );
};

export default AgencyJobSubscriptionPlans;
